import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Database, Cloud, Shield, FileText, CheckCircle2, TrendingUp, Clock, Zap, Server } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const technologies = [
  { name: 'Azure Data Factory', icon: Cloud },
  { name: 'Azure SQL Managed Instance', icon: Database },
  { name: 'Power BI Paginated Reports', icon: FileText },
  { name: 'Azure AD & Key Vault', icon: Shield },
  { name: 'Power Automate', icon: Zap },
  { name: 'Terraform', icon: Server },
];

const keyMetrics = [
  {
    icon: TrendingUp,
    value: '40%',
    label: 'Cost Reduction',
    description: 'In infrastructure and operational overhead post-migration',
  },
  {
    icon: CheckCircle2,
    value: '98%+',
    label: 'Report Conversion',
    description: 'Success rate across 1,000+ SSRS to Power BI Paginated Reports',
  },
  {
    icon: Clock,
    value: '20 Weeks',
    label: 'Full Migration',
    description: 'From assessment to production cutover',
  },
  {
    icon: Zap,
    value: '99.9%',
    label: 'Uptime',
    description: 'Maintained post-cutover with zero data loss during transition',
  },
];

const highlights = [
  {
    category: 'Migration Excellence',
    items: [
      'Migrated 200+ stored procedures to Azure SQL Managed Instance with 98%+ success rate and zero data loss',
      'Converted 1,000+ SSRS reports to Power BI Paginated Reports with 30% faster rendering post-migration',
      'Completed the full digital transformation in 20 weeks from assessment phase through production cutover',
    ],
  },
  {
    category: 'Operational Transformation',
    items: [
      'Reduced infrastructure costs by 40% through cloud-native pay-as-you-go architecture',
      'Cut maintenance effort by 50% through automated orchestration, monitoring, and patch management',
      'Achieved 25% faster ETL processing after migration from SSIS to Azure Data Factory',
    ],
  },
];

const challenges = [
  {
    title: 'High-Risk Migration of Business-Critical Stored Procedures',
    icon: Database,
    problem:
      '200+ stored procedures supported live production operations for an FMCG enterprise — any migration error could halt operations and impact supply chain, inventory, and financial reporting.',
    solution:
      'Conducted comprehensive compatibility analysis, migrated in phased batches with parallel validation runs, and tested all stored procedure functionality post-migration before decommissioning on-premises systems.',
    impact: '98%+ migration success rate with zero data loss and zero production incidents at cutover',
  },
  {
    title: '1,000+ Report Conversion Without User Disruption',
    icon: FileText,
    problem:
      'SSRS reports had varied layouts, data sources, parameterisation logic, and subscription configurations — bulk conversion without a structured framework would produce broken reports and block business operations.',
    solution:
      'Built a systematic conversion framework prioritising reports by business criticality, converting RDL files to Power BI Paginated Report format, updating all data source connections, and validating output against original SSRS versions before go-live.',
    impact: '98%+ conversion success rate with 30% faster rendering and preserved multi-format export support',
  },
  {
    title: 'Complex Multi-Tenant Security Requirements',
    icon: Shield,
    problem:
      'Multiple internal business units required strict data segregation — a misconfigured security model would expose cross-tenant data and create compliance risk across the organisation.',
    solution:
      'Implemented Row-Level Security with dynamic user-based filtering, full Azure AD integration, Key Vault-managed secrets, private endpoints, and comprehensive audit logging across all data access paths.',
    impact: 'Secure multi-tenant access with full IAM governance from day one post-cutover',
  },
  {
    title: 'Single Points of Failure in Legacy Architecture',
    icon: Cloud,
    problem:
      'The on-premises infrastructure had no high-availability design — server failures directly halted ETL processing and report delivery for the entire business with no failover capability.',
    solution:
      'Deployed Azure SQL Managed Instance with high-availability configuration, Azure Data Lake Storage as a resilient data tier, and ADF with automated retry logic — eliminating all single points of failure.',
    impact: '99.9% uptime achieved post-cutover with automated failure recovery across all pipeline and reporting layers',
  },
];

const FMCGMigration = () => {
  return (
    <>
      <SEOHead
        title="FMCG Enterprise Azure Cloud Migration Case Study | Agility AI"
        description="How Agility migrated 200+ stored procedures and 1,000+ SSRS reports to Azure in 20 weeks — 40% cost reduction, 98%+ report conversion, 99.9% uptime, zero data loss."
        url="https://www.agilitytech.ai/case-studies/fmcg-migration"
        type="article"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'FMCG Enterprise Azure Cloud Migration',
          description:
            'Migrating 200+ stored procedures and 1,000+ SSRS reports from legacy on-premises FMCG infrastructure to Microsoft Azure in 20 weeks with zero data loss and 99.9% uptime post-cutover.',
          author: { '@type': 'Organization', name: 'Agility AI' },
          publisher: { '@type': 'Organization', name: 'Agility AI' },
        }}
      />
      <div className="pt-20 bg-black">
        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&q=80"
              alt="FMCG Azure Migration Banner"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
          </div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
                <span className="text-teal-400 text-sm font-medium">FMCG</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                FMCG Enterprise Azure Cloud Migration
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">
                Migrating 200+ stored procedures and 1,000+ SSRS reports from legacy on-premises FMCG infrastructure to Microsoft Azure in 20 weeks with zero data loss and 99.9% uptime post-cutover.
              </p>
            </div>
          </div>
        </section>

        {/* Key Metrics */}
        <section className="py-16 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {keyMetrics.map((metric, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center"
                >
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <metric.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                  <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                  <p className="text-gray-400 text-sm">{metric.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Project Details */}
        <section className="py-12 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">Project Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Industry</h3>
                <p className="text-white">FMCG</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Region</h3>
                <p className="text-white">Global</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Project Size</h3>
                <p className="text-white">Enterprise — 200+ Procedures, 1,000+ Reports</p>
              </div>
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-lg font-semibold text-teal-400 mb-2">Time Frame</h3>
                <p className="text-white">Q1 2025 - Completed</p>
              </div>
            </div>
          </div>
        </section>

        {/* Technologies */}
        <section className="py-12 bg-gray-900 border-t border-gray-800">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-xl font-bold text-white mb-6 text-center">Technology Stack</h3>
            <div className="flex flex-wrap justify-center gap-6">
              {technologies.map((tech, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 bg-gray-800/30 rounded-lg px-4 py-3 border border-gray-700/30"
                >
                  <tech.icon className="h-6 w-6 text-teal-400" />
                  <span className="text-gray-300 font-medium">{tech.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Challenge Overview */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-8">The Challenge</h2>
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <FileText className="h-6 w-6 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">
                      Legacy On-Premises FMCG Infrastructure
                    </h3>
                    <p className="text-xl text-gray-300 leading-relaxed">
                      A large FMCG enterprise operated its entire data platform on aging on-premises infrastructure: 200+ stored procedures running critical business logic, SSAS analytics cubes, and over 1,000 SSRS operational reports. High maintenance overhead consumed significant engineering capacity, manual report distribution created bottlenecks, limited scalability was blocking growth, and single points of failure meant any server issue could halt reporting and ETL operations across the business.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Results */}
        <section className="py-20 bg-black border-t border-gray-800">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">Transformational Results</h2>
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50 mb-12">
                <div className="flex items-start space-x-4 mb-8">
                  <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle2 className="h-6 w-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">
                      Cloud-Native FMCG Data Platform
                    </h3>
                    <p className="text-xl text-gray-300 leading-relaxed">
                      We delivered a complete cloud migration in 20 weeks — migrating 200+ stored procedures with 98%+ success, converting 1,000+ SSRS reports to Power BI Paginated Reports with 30% faster rendering, and rebuilding the entire ETL layer on Azure Data Factory. Post-cutover, infrastructure costs fell 40%, maintenance effort dropped 50%, and the platform maintains 99.9% uptime with automated failure recovery — all with zero data loss through the entire transition.
                    </p>
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                {highlights.map((category, index) => (
                  <div
                    key={index}
                    className="bg-gray-900/30 backdrop-blur-sm rounded-xl p-6 border border-gray-800/30"
                  >
                    <h4 className="text-lg font-bold text-teal-400 mb-4">{category.category}</h4>
                    <div className="space-y-4">
                      {category.items.map((item, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <CheckCircle2 className="flex-shrink-0 h-5 w-5 text-green-400 mt-1" />
                          <p className="text-gray-300 text-sm leading-relaxed">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Challenges & Solutions */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">Challenges & Solutions</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {challenges.map((challenge, index) => (
                <div
                  key={index}
                  className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300"
                >
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
                      <challenge.icon className="h-6 w-6 text-teal-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{challenge.title}</h3>
                  </div>
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-red-400 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-red-400 rounded-full mr-3"></span>Problem
                      </h4>
                      <p className="text-gray-300 leading-relaxed pl-5">{challenge.problem}</p>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-green-400 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>Solution
                      </h4>
                      <p className="text-gray-300 leading-relaxed pl-5">{challenge.solution}</p>
                    </div>
                    <div className="bg-teal-500/10 rounded-lg p-4 border border-teal-500/20">
                      <h4 className="text-sm font-semibold text-teal-400 mb-2">Impact</h4>
                      <p className="text-teal-300 font-medium">{challenge.impact}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="h-8 w-8 text-teal-400" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-6">
                Ready to Migrate Your Data Platform to Azure?
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Contact our cloud migration specialists to discover how a structured, phased Azure migration can modernize your data infrastructure without disrupting operations.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <span>Get Started Today</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FMCGMigration;
